import React, { useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import oauthService from '../services/oauthService';
import './OAuthButtons.css';

const GoogleOAuthButton = ({ onSuccess, onError, buttonText = "Continue with Google", className = "" }) => {
  const [isGoogleLoaded, setIsGoogleLoaded] = useState(false);

  useEffect(() => {
    // Load Google Sign-In API
    const loadGoogleAPI = () => {
      if (window.google) {
        setIsGoogleLoaded(true);
        return;
      }

      const script = document.createElement('script');
      script.src = 'https://accounts.google.com/gsi/client';
      script.async = true;
      script.defer = true;
      script.onload = () => {
        if (window.google) {
          setIsGoogleLoaded(true);
        }
      };
      document.head.appendChild(script);
    };

    loadGoogleAPI();
  }, []);

  useEffect(() => {
    if (isGoogleLoaded && window.google) {
      const googleClientId = process.env.REACT_APP_GOOGLE_CLIENT_ID;
      
      // Check if Google Client ID is configured
      if (!googleClientId) {
        console.warn('Google Sign In not configured: REACT_APP_GOOGLE_CLIENT_ID is missing');
        return;
      }
      
      try {
        // Initialize Google Sign-In
        window.google.accounts.id.initialize({
          client_id: googleClientId,
          callback: handleGoogleResponse,
        });
      } catch (error) {
        console.error('Failed to initialize Google Sign In:', error);
      }
    }
  }, [isGoogleLoaded]);

  const handleGoogleResponse = async (response) => {
    try {
      const result = await oauthService.loginWithGoogle(response.credential);
      
        if (result.success) {
        const userData = result.data || {};

        if (userData.accessToken) {
          localStorage.setItem('authToken', userData.accessToken);
        }
        if (userData.refreshToken) {
          localStorage.setItem('refresh_token', userData.refreshToken);
        }
        if (userData.email) {
          localStorage.setItem('userEmail', userData.email);
        }

        toast.success('Successfully signed in with Google!');
        onSuccess && onSuccess(userData);
      } else {
        throw new Error(result.message || 'Google sign-in failed');
      }
    } catch (error) {
      console.error('Google OAuth error:', error);
      const errorMessage = error.response?.data?.message || error.message || 'Google sign-in failed';
      toast.error(errorMessage);
      onError && onError(error);
    }
  };

  const handleGoogleSignIn = () => {
    if (window.google) {
      try {
        // Try the prompt method first
        window.google.accounts.id.prompt((notification) => {
          console.log('Google prompt notification:', notification);
          if (notification.isNotDisplayed() || notification.isSkippedMoment()) {
            // If prompt fails, try renderButton as fallback
            console.log('Prompt failed, trying button render method');
            renderGoogleButton();
          }
        });
      } catch (error) {
        console.error('Google prompt failed:', error);
        // Fallback to button render
        renderGoogleButton();
      }
    } else {
      toast.error('Google Sign-In is not available');
    }
  };

  const renderGoogleButton = () => {
    const buttonDiv = document.createElement('div');
    buttonDiv.id = 'google-signin-button-temp';
    document.body.appendChild(buttonDiv);

    window.google.accounts.id.renderButton(buttonDiv, {
      theme: 'outline',
      size: 'large',
      width: 250,
    });

    // Auto-click the rendered button
    setTimeout(() => {
      const googleButton = buttonDiv.querySelector('div[role="button"]');
      if (googleButton) {
        googleButton.click();
      }
      // Clean up
      setTimeout(() => {
        if (document.body.contains(buttonDiv)) {
          document.body.removeChild(buttonDiv);
        }
      }, 1000);
    }, 100);
  };

  return (
    <button 
      type="button" 
      onClick={handleGoogleSignIn}
      className={`social-btn google-btn ${className}`}
      disabled={!isGoogleLoaded}
    >
      <svg viewBox="0 0 24 24" className="social-icon">
        <path
          d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
          fill="#4285F4"
        />
        <path
          d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
          fill="#34A853"
        />
        <path
          d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
          fill="#FBBC05"
        />
        <path
          d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
          fill="#EA4335"
        />
      </svg>
      {buttonText}
    </button>
  );
};

export default GoogleOAuthButton;
